/**
 * The board's tools, as one strip on the panel.
 *
 * ── The tools are glyphs now ────────────────────────────────────────────────
 * They were words, and the reason was real: the icon map had no pen, no
 * rectangle and no arrow, and a button with no icon renders as nothing at all,
 * which is worse than a label. The map has them since `iconmap.h` § Boards was
 * copied into it name for name, so the desktop's bar and this one carry one
 * glyph per role.
 *
 * Each button names its glyph as a literal attribute rather than reading it out
 * of a table, because `icons.test.ts` greps the source for `icon="…"`. A name
 * held in a table is a name nothing checks, and what it would catch is the
 * failure this app cannot see: a button that draws nothing, throws nothing and
 * logs nothing.
 *
 * ── Two colours, and what an absent one means ───────────────────────────────
 * The six brand swatches are the quick row and set an opaque outline colour.
 * The two tiles past them are for everything else and carry the alpha channel:
 * a translucent fill is how a shape becomes a highlight rather than a lid over
 * what is under it. **A fill at zero opacity is no fill**, and that mapping is
 * not cosmetic — `geometry.ts` hits an unfilled shape near its line and a
 * filled one anywhere inside, so a fill nobody can see would swallow every
 * click meant for what the shape was drawn around. `colour.ts` holds the wire
 * format and why it is not `#rrggbbaa`.
 */

import React from 'react';
import { ds } from '../../ds';
import { useT } from '../../i18n';
import { inkToWire, parseInk, type Ink } from './colour';

export type Tool = 'select' | 'pen' | 'rect' | 'ellipse' | 'line' | 'arrow' | 'text';

/** The token hexes, because a colour on the wire is read by clients that do not
 *  have this stylesheet. Violet 600, mauve 500, teal 500, warning 500,
 *  danger 500, slate 900. */
export const SWATCHES: { key: string; value: string }[] = [
  { key: 'violet', value: '#796cbf' },
  { key: 'mauve', value: '#886193' },
  { key: 'teal', value: '#0fb3a8' },
  { key: 'amber', value: '#e8a33d' },
  { key: 'rose', value: '#d94f6f' },
  // Slate 500, not the ink of the ramp's dark end: a board is drawn on the
  // window ground, and that ground is near-black in dark mode, where slate 900
  // is a stroke nobody can see. A mid grey is legible on both grounds, which is
  // what a neutral swatch is for. The desktop app's palette says the same.
  { key: 'slate', value: '#9397ab' }
];

/** What a stroke width may be, in world units. The wire has no opinion — the
 *  server validates only that the number is finite — so this range is a
 *  usability choice and nothing more, and it is `boardview.h`'s. */
export const MIN_WIDTH = 1;
export const MAX_WIDTH = 48;
export const DEFAULT_WIDTH = 2;

export const clampWidth = (asked: number): number =>
  Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(asked)));

/** What a fill opens at when there was none. The fill somebody reaches for is
 *  nearly always a wash of the line they have just drawn. */
const WASH = 0.35;

export interface ToolbarProps {
  tool: Tool;
  onTool: (tool: Tool) => void;
  color: string;
  onColor: (color: string) => void;
  /** `''` is no fill, and is the default. */
  fill: string;
  onFill: (fill: string) => void;
  width: number;
  onWidth: (width: number) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onFit: () => void;
  onExport: () => void;
  onDeleteSelected: () => void;
  hasSelection: boolean;
}

const GROUP: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2, 2px)'
};

function ToolButton({
  icon,
  tool,
  active,
  onTool
}: {
  icon: string;
  tool: Tool;
  active: Tool;
  onTool: (tool: Tool) => void;
}) {
  const { IconButton } = ds();
  const t = useT();
  return (
    <IconButton
      icon={icon}
      label={t(`boards.tool.${tool}`)}
      size="sm"
      variant="ghost"
      active={active === tool}
      onClick={() => onTool(tool)}
    />
  );
}

/** A colour and its opacity. The two are separate callbacks because picking a
 *  colour and setting an opacity mean different things to a fill: one of them
 *  is how somebody turns a fill back on. */
function InkControl({
  ink,
  colourLabel,
  opacityLabel,
  onColour,
  onOpacity
}: {
  ink: Ink;
  colourLabel: string;
  opacityLabel: string;
  onColour: (hex: string) => void;
  onOpacity: (alpha: number) => void;
}) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4, 4px)' }}>
      <input
        className="rl-ink"
        type="color"
        value={ink.hex}
        aria-label={colourLabel}
        title={colourLabel}
        onChange={(e) => onColour(e.target.value)}
      />
      <input
        className="rl-alpha"
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={ink.alpha}
        aria-label={opacityLabel}
        title={opacityLabel}
        onChange={(e) => onOpacity(Number(e.target.value))}
      />
    </span>
  );
}

export function Toolbar(props: ToolbarProps) {
  const { Button, IconButton } = ds();
  const t = useT();

  const stroke = parseInk(props.color) ?? { hex: SWATCHES[0]!.value, alpha: 1 };
  // No fill shows the outline's colour at nothing, so raising the slider from
  // there gives a wash of the line rather than a colour nobody chose.
  const fill = parseInk(props.fill) ?? { hex: stroke.hex, alpha: 0 };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'var(--space-8)',
        minHeight: 44,
        padding: '0 var(--space-12)',
        background: 'var(--app-panel-alt)',
        borderBottom: '1px solid var(--border-subtle)'
      }}
    >
      <div style={GROUP}>
        <ToolButton icon="ToolSelect" tool="select" active={props.tool} onTool={props.onTool} />
        <ToolButton icon="ToolPen" tool="pen" active={props.tool} onTool={props.onTool} />
        <ToolButton icon="ToolRect" tool="rect" active={props.tool} onTool={props.onTool} />
        <ToolButton icon="ToolEllipse" tool="ellipse" active={props.tool} onTool={props.onTool} />
        <ToolButton icon="ToolLine" tool="line" active={props.tool} onTool={props.onTool} />
        <ToolButton icon="ToolArrow" tool="arrow" active={props.tool} onTool={props.onTool} />
        <ToolButton icon="ToolText" tool="text" active={props.tool} onTool={props.onTool} />
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-4, 4px)' }}>
        {SWATCHES.map((swatch) => (
          <button
            key={swatch.key}
            type="button"
            aria-label={t(`boards.colour.${swatch.key}`)}
            title={t(`boards.colour.${swatch.key}`)}
            aria-pressed={props.color === swatch.value}
            onClick={() => props.onColor(swatch.value)}
            style={{
              width: 24,
              height: 24,
              borderRadius: 10,
              background: swatch.value,
              cursor: 'pointer',
              border:
                props.color === swatch.value
                  ? '2px solid var(--text-strong)'
                  : '1px solid var(--border-subtle)'
            }}
          />
        ))}
      </div>

      <InkControl
        ink={stroke}
        colourLabel={t('boards.colour.outline')}
        opacityLabel={t('boards.opacity.outline')}
        onColour={(hex) => props.onColor(inkToWire({ hex, alpha: stroke.alpha }))}
        onOpacity={(alpha) => props.onColor(inkToWire({ hex: stroke.hex, alpha }))}
      />

      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4, 4px)' }}>
        <InkControl
          ink={fill}
          colourLabel={t('boards.colour.fill')}
          opacityLabel={t('boards.opacity.fill')}
          onColour={(hex) =>
            props.onFill(inkToWire({ hex, alpha: fill.alpha > 0 ? fill.alpha : WASH }))
          }
          onOpacity={(alpha) =>
            props.onFill(alpha <= 0 ? '' : inkToWire({ hex: fill.hex, alpha }))
          }
        />
        <IconButton
          icon="Close"
          label={t('boards.colour.none')}
          size="sm"
          variant="ghost"
          active={props.fill === ''}
          onClick={() => props.onFill('')}
        />
      </span>

      <input
        className="rl-board-width"
        type="number"
        min={MIN_WIDTH}
        max={MAX_WIDTH}
        step={1}
        value={props.width}
        aria-label={t('boards.width')}
        title={t('boards.width')}
        onChange={(e) => props.onWidth(clampWidth(Number(e.target.value)))}
      />

      <div style={{ ...GROUP, marginLeft: 'auto' }}>
        <IconButton
          icon="Undo"
          label={t('boards.undo')}
          size="sm"
          variant="ghost"
          disabled={!props.canUndo}
          onClick={props.onUndo}
        />
        <IconButton
          icon="Redo"
          label={t('boards.redo')}
          size="sm"
          variant="ghost"
          disabled={!props.canRedo}
          onClick={props.onRedo}
        />
        <IconButton
          icon="BoardFit"
          label={t('boards.fit')}
          size="sm"
          variant="ghost"
          onClick={props.onFit}
        />
        {/* Labelled, because neither app has a glyph for it: the desktop's bar
            has no export at all, and inventing a name for one is how a button
            comes out blank. */}
        <Button size="sm" variant="ghost" onClick={props.onExport}>
          {t('boards.export')}
        </Button>
        <IconButton
          icon="Delete"
          label={t('boards.delete.element')}
          size="sm"
          variant="ghost"
          disabled={!props.hasSelection}
          onClick={props.onDeleteSelected}
        />
      </div>
    </div>
  );
}
